import { useEffect, useState } from 'react';
import type { CategoryTree } from 'src/types';
import queryBuilder from '../../query';
import type { RouterParams } from '../../types';
import type { ResponseData } from '../data/use-with-elastic';
import { useElasticFetch } from '../data/use-with-elastic';

const RATING_INTERVAL_SIZE = 2;

const createRatingFilter = (
  field: string,
  selectedCategories: string[],
  selectedStores: string[]
) => ({
  filter: {
    bool: {
      must: [
        {
          range: {
            [field]: {
              gt: 1,
            },
          },
        },
        selectedCategories.length && {
          terms: {
            'product.category.path.id': selectedCategories,
          },
        },
        selectedStores.length && {
          terms: {
            'store.id': selectedStores,
          },
        },
      ].filter(x => x),
    },
  },
  aggs: {
    bars: {
      histogram: {
        field,
        interval: RATING_INTERVAL_SIZE,
        extended_bounds: {
          min: 0,
          max: 10,
        },
      },
    },
  },
});

type AggregationFields = {
  visibleCategories: string[];
  selectedCategories: string[];
  selectedStores: string[];
  maxStores: number;
};

const aggregationFields = ({
  visibleCategories,
  selectedCategories,
  selectedStores,
  maxStores,
}: AggregationFields) => ({
  stores: {
    filter: {
      bool: {
        must: [
          selectedCategories.length && {
            terms: {
              'product.category.path.id': selectedCategories,
            },
          },
        ].filter(x => x),
      },
    },
    aggs: {
      current: {
        terms: { field: 'store.id', size: maxStores },
        aggs: {
          top_hits: {
            top_hits: {
              _source: ['store.id', 'store.name'],
              size: 1,
            },
          },
        },
      },
    },
  },
  category: {
    filter: {
      bool: {
        must: [
          { terms: { 'product.category.path.id': visibleCategories } },
          selectedStores.length && {
            terms: {
              'store.id': selectedStores,
            },
          },
        ].filter(x => x),
      },
    },
    aggs: {
      current: {
        terms: {
          field: 'product.category.path.id',
          include: visibleCategories,
          size: visibleCategories.length,
        },
      },
    },
  },

  prices: {
    filter: {
      bool: {
        must: [
          selectedCategories.length && {
            terms: {
              'product.category.path.id': selectedCategories,
            },
          },
          selectedStores.length && {
            terms: {
              'store.id': selectedStores,
            },
          },
        ].filter(x => x),
      },
    },
    aggs: {
      max_price: {
        max: {
          field: 'price.offer',
        },
      },
      min_price: {
        min: {
          field: 'price.offer',
        },
      },
    },
  },

  store_rating: createRatingFilter(
    'store.user_reviews.rating',
    selectedCategories,
    selectedStores
  ),
  product_rating: createRatingFilter(
    'product.user_reviews.rating',
    selectedCategories,
    selectedStores
  ),
});

const getVisibleCategories = (tree: CategoryTree[], expanded: string[]) => {
  const selected: string[] = [];

  const pusher = (child: any) => {
    selected.push(child.id);

    if (child.children && expanded.includes(child.id)) {
      child.children.forEach(pusher);
    }
  };

  tree.forEach(pusher);

  return selected;
};

type BodyType = {
  size: number;
  aggs: object;
  query: {
    bool: {
      must: object[];
    };
  };
};

const buildQuery = (props: AggregationsProps) => {
  const router = props.router;

  const levelOne = getVisibleCategories(
    props.categoryTree,
    props.expandedCategories
  );

  const aggs = aggregationFields({
    selectedCategories: props.router.categories,
    visibleCategories: getVisibleCategories(props.categoryTree, levelOne),
    selectedStores: props.router.stores,
    maxStores: props.router.maxStores,
  });

  const body: BodyType = {
    size: 0,
    aggs,
    query: {
      bool: {
        must: [],
      },
    },
  };

  if (router.query) {
    body.query.bool.must.push(queryBuilder(router.query));
  }

  return body;
};

type AggregationsProps = {
  router: RouterParams;
  categoryTree: any;
  expandedCategories: string[];
  onSuccess: (e: ResponseData) => void;
  campaignId: number;
  render: (e: ResponseData) => JSX.Element | null;
};

export const Aggregations = (props: AggregationsProps) => {
  const [responseData, setResponseData] = useState<ResponseData>();
  const [currentQuery, setCurrentQuery] = useState(buildQuery(props));
  const fetchResponse = useElasticFetch({
    query: currentQuery,
    campaignId: props.campaignId,
    timeout: 500,
  });

  useEffect(() => {
    setCurrentQuery(buildQuery(props));
  }, [props.router]);

  useEffect(() => {
    if (fetchResponse.fetching || responseData === fetchResponse.data) {
      return;
    }

    props.onSuccess(fetchResponse.data);
    setResponseData(fetchResponse.data);
  }, [fetchResponse]);

  if (!responseData) {
    return null;
  }

  return props.render(responseData);
};
